/**
 * Compile-time locale-contract enforcement.
 *
 * WHY THIS FILE EXISTS
 * The runtime locale loader (`LanguageContext.tsx`) pulls locale JSON through
 * `import.meta.glob<{ default: unknown }>` and then *casts* the result to the
 * corresponding `Translations[...]` slice. A cast is NOT a type-check: TypeScript
 * happily accepts a `standWorkspace` locale that is missing a REQUIRED block,
 * and the gap would only surface at runtime as silently rendered hardcoded
 * English.
 *
 * This module restores the missing static guarantee WITHOUT touching the runtime
 * loader. Each `standWorkspace` locale JSON is imported statically (resolveJsonModule
 * is enabled) and its required blocks are pinned with `satisfies` to their
 * contracts. Because the import is static, deleting a required block (or any of
 * its required keys) from ANY of the en/fi/sv/es `standWorkspace.json` files
 * makes `npm run type-check` fail.
 *
 * It is intentionally side-effect-free at runtime: the values are only used to
 * drive the `satisfies` checks. A single `void`-reference keeps the imports from
 * being elided so the type assertions are actually evaluated.
 */
import type {
  StandWorkspaceHistoryTimelineTranslations,
  StandWorkspaceBranchOperationsTranslations,
  TimberPricesTranslations,
  CommonTranslations,
  BrowsePropertiesTranslations,
} from './types';

import enStandWorkspace from './locales/en/standWorkspace.json';
import fiStandWorkspace from './locales/fi/standWorkspace.json';
import svStandWorkspace from './locales/sv/standWorkspace.json';
import esStandWorkspace from './locales/es/standWorkspace.json';
import enTimberPrices from './locales/en/timberPrices.json';
import fiTimberPrices from './locales/fi/timberPrices.json';
import svTimberPrices from './locales/sv/timberPrices.json';
import esTimberPrices from './locales/es/timberPrices.json';
import enCommon from './locales/en/common.json';
import fiCommon from './locales/fi/common.json';
import svCommon from './locales/sv/common.json';
import esCommon from './locales/es/common.json';
import enBrowse from './locales/en/browseProperties.json';
import fiBrowse from './locales/fi/browseProperties.json';
import svBrowse from './locales/sv/browseProperties.json';
import esBrowse from './locales/es/browseProperties.json';

// The loader casts JSON, so check the owner-flow copy before it can ship.
const ownerFlowContracts = [
  enCommon.myForests satisfies CommonTranslations['myForests'],
  fiCommon.myForests satisfies CommonTranslations['myForests'],
  svCommon.myForests satisfies CommonTranslations['myForests'],
  esCommon.myForests satisfies CommonTranslations['myForests'],
  enBrowse satisfies BrowsePropertiesTranslations,
  fiBrowse satisfies BrowsePropertiesTranslations,
  svBrowse satisfies BrowsePropertiesTranslations,
  esBrowse satisfies BrowsePropertiesTranslations,
] as const;

/**
 * The inspection map's degraded copy. These are the only sentences that tell an
 * owner why the cadastral overlay is not drawn; a locale that loses one would
 * render a blank banner segment — the exact silence the capability state exists
 * to remove — so their absence must be a compile error, not a runtime surprise.
 */
interface RequiresInspectionMapDegradedCopy {
  cadastralUnavailableMessage: string;
  cadastralUnsupportedMessage: string;
  officialTerrainFallbackMessage: string;
  someLayersUnavailableTitle: string;
}

/**
 * Blocks every `standWorkspace` locale must expose. Pinning the whole slice is
 * impractical (several keys are optional / vary by locale and are cast at
 * runtime), but these blocks are mandatory and their absence is a compile error.
 */
interface RequiresStandWorkspaceBlocks {
  // the modernized History & future timeline block is
  // mandatory in every locale; a missing key here is a compile error.
  historyTimeline: StandWorkspaceHistoryTimelineTranslations;
  branchOperations: StandWorkspaceBranchOperationsTranslations;
  inspectionMap: RequiresInspectionMapDegradedCopy;
}

// COMPILE-TIME ASSERTIONS — a missing or incomplete required block in any of
// these locale JSONs is a TypeScript error here.
const enContract = enStandWorkspace satisfies RequiresStandWorkspaceBlocks;
const fiContract = fiStandWorkspace satisfies RequiresStandWorkspaceBlocks;
const svContract = svStandWorkspace satisfies RequiresStandWorkspaceBlocks;
const esContract = esStandWorkspace satisfies RequiresStandWorkspaceBlocks;

const enTimberPricesContract = enTimberPrices satisfies TimberPricesTranslations;
const fiTimberPricesContract = fiTimberPrices satisfies TimberPricesTranslations;
const svTimberPricesContract = svTimberPrices satisfies TimberPricesTranslations;
const esTimberPricesContract = esTimberPrices satisfies TimberPricesTranslations;

/**
 * Tuple of the contract-checked locale `standWorkspace` blocks. Exported (and
 * referenced) purely so the static imports above are retained and the
 * `satisfies` assertions are evaluated; this module performs no runtime work.
 */
export const STAND_WORKSPACE_LOCALE_CONTRACTS = [
  ...ownerFlowContracts,
  enContract,
  fiContract,
  svContract,
  esContract,
  enTimberPricesContract,
  fiTimberPricesContract,
  svTimberPricesContract,
  esTimberPricesContract,
] as const;
